// Email the whole POS dataset as a JSON backup attachment.
//
// All app state lives in IndexedDB under the `sconnect.` prefix. We serialise
// every such key into one JSON file (same format as services/folderBackup.ts and
// services/cloudBackup.ts) and hand it to the device's email/share flow.
//
// There is no SMTP backend, so we can't post mail ourselves. Instead we use the
// Web Share API where available — on the Android shell and mobile browsers this
// opens the system share sheet so the user can pick Gmail/Outlook with the JSON
// already attached. On desktop browsers (no file share) we fall back to
// downloading the file and opening the mail client with a prefilled message for
// the user to attach it manually.

import { idbAllKeys, idbGetRaw, cachedRawKey } from './idb';

const PREFIX = 'sconnect.';

// Where the chosen recipient address is remembered. Kept OUTSIDE the `sconnect.`
// namespace so restoring a snapshot doesn't clobber it.
const emailCache = cachedRawKey('epos.backupEmail', '');

export function getBackupEmail(): string {
  return emailCache.get();
}

export function setBackupEmail(email: string): void {
  emailCache.set(email.trim());
}

// Snapshot every POS collection currently stored.
async function collectLocalData(): Promise<Record<string, unknown>> {
  const dump: Record<string, unknown> = {};
  for (const key of await idbAllKeys()) {
    if (!key.startsWith(PREFIX)) continue;
    const raw = await idbGetRaw(key);
    if (raw == null) continue;
    try {
      dump[key] = JSON.parse(raw);
    } catch {
      /* skip non-JSON values */
    }
  }
  return dump;
}

function backupFilename(): string {
  const stamp = new Date().toISOString().replace(/[:.]/g, '-').split('T');
  return `epos-backup-${stamp[0]}_${stamp[1].slice(0, 8)}.json`;
}

export interface EmailBackupResult {
  // 'share' = handed to the OS share sheet with the file attached.
  // 'mailto' = file downloaded + mail client opened for manual attachment.
  method: 'share' | 'mailto';
  filename: string;
}

// Build the backup file and start the email/share flow for `to`. Throws with
// name 'AbortError' if the user cancels the native share sheet.
export async function emailBackup(to: string): Promise<EmailBackupResult> {
  const recipient = to.trim();
  const filename = backupFilename();
  const content = JSON.stringify(await collectLocalData(), null, 2);
  const subject = `EPOS data backup ${new Date().toLocaleDateString()}`;
  const body = `Attached is the EPOS POS data backup created on ${new Date().toLocaleString()}.`;

  const file = new File([content], filename, { type: 'application/json' });

  // Preferred path: native / Web Share sheet with the file attached.
  const nav = navigator as Navigator & { canShare?: (data: ShareData) => boolean };
  if (nav.share && nav.canShare && nav.canShare({ files: [file] })) {
    await nav.share({ files: [file], title: subject, text: body });
    return { method: 'share', filename };
  }

  // Fallback: download the file, then open the mail client prefilled so the user
  // attaches the just-downloaded backup.
  const url = URL.createObjectURL(file);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);

  const mailBody = `${body}\n\n(Attach the downloaded file "${filename}".)`;
  window.location.href =
    `mailto:${encodeURIComponent(recipient)}` +
    `?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(mailBody)}`;
  return { method: 'mailto', filename };
}
